import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Mail, Phone, Shield, MapPin } from 'lucide-react';
import { Wordmark, Eyebrow, ConsultationForm, buildLeadConfirmationEmail } from './shared';

const trustPoints = [
  'Built exclusively for pathology by pathologists',
  '100% US-based team with no overseas employees or data handling',
  'Competitive pricing with no hidden fees or start up costs',
  'Live dashboard so you always know where your revenue stands',
];

const stats = [
  { val: 'Competitive Pricing', lbl: 'Full-service billing · no hidden fees' },
  { val: '> 92%', lbl: 'Average 12-month net collection rate' },
  { val: '100%', lbl: 'US-based team & operations' },
];

function LeadPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? '';

    document.title = 'Free Pathology Billing Consultation | Horizon RCM';
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Request a free consultation with Horizon RCM — exclusive, US-based revenue cycle management for pathology laboratories. No obligation.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className='min-h-screen bg-brand-cream text-brand-charcoal font-body'>
      <header className='border-b border-brand-coffee/10 bg-brand-warmwhite/95 backdrop-blur-sm'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-6'>
          <Link to='/' className='text-left'>
            <Wordmark />
          </Link>
          <a
            href='tel:+18882576123'
            className='hidden sm:flex items-center gap-2 font-body text-sm font-medium text-brand-coffee hover:text-brand-ember transition-colors'
          >
            <Phone size={16} className='text-brand-ember' />
            (888) 257-6123
          </a>
        </div>
      </header>

      <main className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start'>
          <div className='lg:col-span-6'>
            <Eyebrow className='text-brand-ember mb-4'>Free Consultation</Eyebrow>
            <h1 className='font-display font-bold text-brand-coffee text-4xl sm:text-5xl leading-[1.1] mb-6'>
              Pathology billing that{' '}
              <em className='text-brand-ember'>chases every dollar</em>
            </h1>
            <p className='font-body text-lg text-brand-stone leading-relaxed mb-8'>
              Horizon RCM is a US-based revenue cycle management partner built
              exclusively for pathology laboratories — from molecular diagnostics
              to anatomic pathology. We manage billing end to end so your team
              can stay focused on patient care, not denials.
            </p>
            <p className='font-body text-lg text-brand-stone leading-relaxed mb-10'>
              Share your email and we'll reach out to discuss your lab's payer
              mix, current challenges, and whether Horizon RCM is the right fit.
              No pressure, no obligation.
            </p>

            <ul className='space-y-4 mb-12'>
              {trustPoints.map((point, i) => (
                <li key={i} className='flex items-start gap-3'>
                  <CheckCircle
                    className='text-brand-ember flex-shrink-0 mt-0.5'
                    size={18}
                  />
                  <span className='font-body text-brand-charcoal/90 leading-relaxed'>
                    {point}
                  </span>
                </li>
              ))}
            </ul>

            <div className='grid grid-cols-3 gap-6 pt-8 border-t border-brand-coffee/15'>
              {stats.map((stat, i) => (
                <div key={i}>
                  <div className='font-display font-bold text-brand-ember text-2xl sm:text-3xl mb-2'>
                    {stat.val}
                  </div>
                  <div
                    className='font-body text-[9px] sm:text-[10px] font-semibold uppercase text-brand-stone leading-relaxed'
                    style={{ letterSpacing: '0.14em' }}
                  >
                    {stat.lbl}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className='lg:col-span-6 lg:sticky lg:top-8'>
            <div className='bg-brand-warmwhite border border-brand-coffee/10 p-8 sm:p-12 shadow-sm'>
              <ConsultationForm
                idPrefix='lead'
                source='New consultation request from Google ad landing page.'
                title='Request Your Free Consultation'
                description='Enter your work email and a member of our team will follow up within one business day.'
                emailBuilder={buildLeadConfirmationEmail}
                emailSubject='Your Free Consultation Request – Horizon RCM'
              />
            </div>

            <div className='mt-8 space-y-6 px-2'>
              <div className='flex items-center gap-3'>
                <Shield size={18} className='text-brand-ember flex-shrink-0' />
                <p className='font-body text-sm text-brand-stone'>
                  HIPAA-compliant · Your information is never shared or sold
                </p>
              </div>
              <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8'>
                <a
                  href='mailto:main@horizon-rcm.com'
                  className='inline-flex items-center gap-2 font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors'
                >
                  <Mail size={16} className='text-brand-ember' />
                  main@horizon-rcm.com
                </a>
                <a
                  href='tel:+18882576123'
                  className='inline-flex items-center gap-2 font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors sm:hidden'
                >
                  <Phone size={16} className='text-brand-ember' />
                  (888) 257-6123
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className='bg-brand-navy py-10 border-t-4 border-brand-ember'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6'>
          <Wordmark onDark />
          <div className='text-center sm:text-right'>
            <Link
              to='/'
              className='font-body text-sm text-brand-cream/70 hover:text-brand-ember transition-colors'
            >
              Explore the full site →
            </Link>
            <p className='font-body text-brand-cream/40 text-xs mt-3 flex items-center justify-center sm:justify-end gap-1.5'>
              <MapPin size={12} />
              100% US-Based · Exclusive Pathology Billing
            </p>
          </div>
        </div>
        <p className='font-body text-brand-cream/40 text-xs text-center mt-8'>
          © 2025 Horizon RCM Partners LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default LeadPage;
